import React, { useEffect } from 'react';
import TransformBox from './TransformBox';
import TextElement from './TextElement';
import ImageElement from './ImageElement';
import FrameElement from './FrameElement';
import {
  getAssetDragData,
  getAssetDragDataFromPlain,
  hasAssetDragData,
} from '../utils/dragDropUtils';
import { hexToRgba, normalizeHex } from '../utils/colorUtils';
import {
  getShapeFillPaint,
  getShapeStrokePaint,
  getTextPaint,
  normalizePaint,
  paintToCss,
  paintToTextStyles,
} from '../utils/paintUtils';

const CUSTOM_SHAPE_PATHS = {
  triangle: 'M50 12 L90 88 L10 88 Z',
  diamond: 'M50 8 L92 50 L50 92 L8 50 Z',
  star: 'M50 10 L61 38 L90 38 L66 57 L75 86 L50 70 L25 86 L34 57 L10 38 L39 38 Z',
  speech:
    'M20 18H80A10 10 0 0 1 90 28V60A10 10 0 0 1 80 70H46L30 84V70H20A10 10 0 0 1 10 60V28A10 10 0 0 1 20 18Z',
  badge:
    'M50 8 L62 16 L76 14 L84 26 L96 32 L92 46 L100 54 L92 62 L96 76 L84 80 L76 92 L62 90 L50 100 L38 90 L24 92 L16 80 L4 76 L8 62 L0 54 L8 46 L4 32 L16 26 L24 14 L38 16 Z',
  blob:
    'M58 12C72 18 84 32 88 48C92 64 86 82 72 90C58 98 36 100 22 90C8 80 2 60 6 44C10 28 24 16 40 12C46 10 52 10 58 12Z',
};

const buildSvgGradient = (paint, id) => {
  const normalized = normalizePaint(paint);
  if (normalized.type !== 'gradient') return null;
  const stops = normalized.stops.map((stop, index) => (
    <stop
      key={`${id}-stop-${index}`}
      offset={`${stop.pos}%`}
      stopColor={hexToRgba(normalizeHex(stop.color) || '#FFFFFF', (stop.alpha ?? 100) / 100)}
    />
  ));
  if (normalized.kind === 'radial') {
    return (
      <radialGradient id={id} cx="50%" cy="50%" r="50%">
        {stops}
      </radialGradient>
    );
  }
  const angle = Number.isFinite(normalized.angle) ? normalized.angle : 0;
  return (
    <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform={`rotate(${angle} 0.5 0.5)`}>
      {stops}
    </linearGradient>
  );
};

const getPaintSolidCss = (paint, fallback = '#111111') => {
  const normalized = normalizePaint(paint, fallback);
  if (normalized.type === 'solid') {
    return paintToCss(normalized);
  }
  const first = normalized.stops?.[0];
  if (!first) return paintToCss({ type: 'solid', color: fallback, alpha: 100 });
  return hexToRgba(normalizeHex(first.color) || fallback, (first.alpha ?? 100) / 100);
};

const useNonPassiveWheel = (viewportRef, handleWheelZoom, onWheelOverride) => {
  useEffect(() => {
    const node = viewportRef?.current;
    if (!node || !handleWheelZoom) return undefined;
    const onWheel = (event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      if (onWheelOverride && onWheelOverride(event) === true) {
        return;
      }
      handleWheelZoom(event);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [handleWheelZoom, onWheelOverride, viewportRef]);
};

const CanvasStage = ({
  viewportRef,
  stageRef,
  artboardRef,
  artboardWidth,
  artboardHeight,
  pageSettings,
  elements,
  selectedIds,
  editingTextId,
  editingTextValue,
  onElementPointerDown,
  onTextStartEdit,
  onTextChange,
  onTextCommit,
  onTextCancel,
  onWorkspacePointerDown,
  onWorkspacePointerMove,
  onWorkspacePointerUp,
  showGuides,
  stageControls,
  isSpacePressed,
  selectionBounds,
  marquee,
  resizeHud,
  snapGuides,
  onTransformHandlePointerDown,
  onRotatePointerDown,
  onAssetDrop,
  imageToolPanel,
  eraserMode,
  eyedropperActive,
  onWheelOverride,
  cropTarget,
  cropRect,
  onCropPointerDown,
  frameCropState,
  onFrameCropPointerDown,
  hoveredFrameId,
  debugMode,
  activeEraserStroke,
  eraserCursor,
  onImageEraserPointerDown,
  isBackgroundRepositioning,
  backgroundImageOverride,
}) => {
  const { zoom, pan, isPanning, startPan, updatePan, endPan } = stageControls;
  const selectedElements = elements.filter((el) => selectedIds.includes(el.id));
  const isTextSelection =
    selectedElements.length > 0 && selectedElements.every((el) => el.type === 'text');
  const showMargin = pageSettings?.showMargin;
  const showBleed = pageSettings?.showBleed;
  const showGrid = pageSettings?.showGrid;
  const grid = pageSettings?.grid || {};
  const backgroundImage = backgroundImageOverride ?? pageSettings?.backgroundImage;
  const showBackgroundImage = Boolean(backgroundImage?.enabled && backgroundImage?.src);
  const bgOpacity = Number.isFinite(backgroundImage?.opacity) ? backgroundImage.opacity : 100;
  const bgNaturalW = backgroundImage?.naturalW || artboardWidth;
  const bgNaturalH = backgroundImage?.naturalH || artboardHeight;
  const bgScale = backgroundImage?.scale || 1;
  const bgImageW = bgNaturalW * bgScale;
  const bgImageH = bgNaturalH * bgScale;
  const marginInset = Number(pageSettings?.margin) > 0
    ? Number(pageSettings.margin)
    : Math.round(Math.min(artboardWidth, artboardHeight) * 0.1);
  const bleedInset = Number(pageSettings?.bleed) > 0 ? Number(pageSettings.bleed) : 0;

  const getGridStyle = () => {
    if (!showGrid) return null;
    const size = Number(grid.size) || 50;
    const opacity = Number(grid.opacity) || 0.2;
    if (grid.type === 'lines') {
      return {
        backgroundImage: `
          linear-gradient(to right, rgba(0,0,0,${opacity}) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(0,0,0,${opacity}) 1px, transparent 1px)
        `,
        backgroundSize: `${size}px ${size}px`,
      };
    }
    return {
      backgroundImage: `radial-gradient(rgba(0,0,0,${opacity}) 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
    };
  };

  const getBackgroundStyle = () => {
    const backgroundPaint = pageSettings?.backgroundPaint;
    if (backgroundPaint) {
      const normalized = normalizePaint(backgroundPaint, '#FFFFFF');
      if (normalized.type === 'gradient') {
        return {
          backgroundImage: paintToCss(normalized),
          backgroundColor: 'transparent',
        };
      }
      return { backgroundColor: paintToCss(normalized) };
    }

    const background = pageSettings?.background;
    if (!background) return { background: '#ffffff' };
    const alpha = typeof background.alpha === 'number'
      ? Math.min(Math.max(background.alpha, 0), 100) / 100
      : 1;
    if (background.type === 'gradient') {
      const gradient = background.gradient || {};
      const stops = Array.isArray(gradient.stops)
        ? gradient.stops
            .slice(0, 4)
            .map((stop) => {
              const color = hexToRgba(normalizeHex(stop.color) || '#FFFFFF', alpha);
              const pos = Number.isFinite(stop.pos) ? stop.pos : 0;
              return `${color} ${pos}%`;
            })
        : [];
      if (!stops.length) {
        return { background: '#ffffff' };
      }
      if (gradient.kind === 'radial') {
        return { background: `radial-gradient(circle, ${stops.join(', ')})` };
      }
      const angle = Number.isFinite(gradient.angle) ? gradient.angle : 0;
      return { background: `linear-gradient(${angle}deg, ${stops.join(', ')})` };
    }
    return {
      backgroundColor: hexToRgba(
        normalizeHex(background.color) || '#FFFFFF',
        alpha,
      ),
    };
  };

  const handleWorkspacePointerDown = (event) => {
    const shouldPan = isSpacePressed || event.button === 1;
    if (shouldPan) {
      event.preventDefault();
      startPan(event);
      return;
    }
    onWorkspacePointerDown(event);
  };

  const handleWorkspacePointerMove = (event) => {
    if (isPanning) {
      event.preventDefault();
      updatePan(event);
      return;
    }
    onWorkspacePointerMove(event);
  };

  const handleWorkspacePointerUp = (event) => {
    if (isPanning) {
      endPan(event);
      return;
    }
    onWorkspacePointerUp(event);
  };

  const handleDragOver = (event) => {
    if (!event?.dataTransfer) return;
    // Always allow drop so onDrop fires reliably (some browsers hide custom types on dragover).
    event.preventDefault();
    event.dataTransfer.dropEffect = hasAssetDragData(event) ? 'copy' : 'copy';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (onAssetDrop) {
      const data = getAssetDragData(event) || getAssetDragDataFromPlain(event);
      onAssetDrop(data?.assetId || null, event);
    }
  };

  const renderElement = (element) => {
    const isSelected = selectedIds.includes(element.id);
    const isEraserTarget =
      imageToolPanel?.open
      && imageToolPanel?.tool === 'eraser'
      && imageToolPanel?.targetId === element.id;
    const isCropTarget =
      imageToolPanel?.open
      && imageToolPanel?.tool === 'crop'
      && imageToolPanel?.targetId === element.id;
    const baseStyle = {
      left: element.x,
      top: element.y,
      width: element.w,
      height: element.h,
      transform: `rotate(${element.rotation || 0}deg)`,
      opacity: element.opacity ?? 1,
      zIndex: element.zIndex ?? 1,
    };

    const commonProps = {
      key: element.id,
      'data-element-id': element.id,
      style: baseStyle,
      onPointerDown: (event) => {
        if (isBackgroundRepositioning) {
          onWorkspacePointerDown(event);
          return;
        }
        if (isSpacePressed || event.button === 1) {
          handleWorkspacePointerDown(event);
          return;
        }
        event.stopPropagation();
        if (isEraserTarget && onImageEraserPointerDown) {
          onImageEraserPointerDown(event, element);
          return;
        }
        if (isCropTarget) {
          return;
        }
        onElementPointerDown(event, element.id);
      },
    };

    if (element.type === 'text') {
      const autoWidth = element.autoWidth !== false;
      const textPaint = getTextPaint(element);
      return (
        <TextElement
          key={element.id}
          element={element}
          isSelected={isSelected}
          isEditing={editingTextId === element.id}
          draftText={editingTextValue}
          isSpacePressed={isSpacePressed}
          onSelect={(event) => onElementPointerDown(event, element.id)}
          onStartEdit={() => onTextStartEdit(element.id)}
          onChange={onTextChange}
          onCommit={onTextCommit}
          onCancel={onTextCancel}
          style={{
            ...baseStyle,
            fontFamily: element.fontFamily || 'Inter, sans-serif',
            fontSize: element.fontSize || 32,
            fontWeight: element.fontWeight || 400,
            fontStyle: element.fontStyle || 'normal',
            textDecoration: element.underline ? 'underline' : 'none',
            letterSpacing: element.letterSpacing ? `${element.letterSpacing}px` : '0px',
            padding: `${element.paddingY || 0}px ${element.paddingX || 0}px`,
            ...(paintToTextStyles(textPaint)),
            textAlign: element.align || 'left',
            lineHeight: element.lineHeight || 1.2,
            whiteSpace: autoWidth ? 'pre' : 'pre-wrap',
            wordBreak: autoWidth ? 'normal' : 'break-word',
            overflowWrap: autoWidth ? 'normal' : 'break-word',
            direction: 'ltr',
            unicodeBidi: 'plaintext',
          }}
        />
      );
    }

    if (element.type === 'shape') {
      const shapeType = element.shapeType || 'rect';
      const isLine = shapeType === 'line';
      const isArrow = shapeType === 'arrow';
      const isGraphic = shapeType === 'graphic' && Boolean(element.svg);
      const flipX = element.flipX ? -1 : 1;
      const flipY = element.flipY ? -1 : 1;
      const transform = `rotate(${element.rotation || 0}deg) scale(${flipX}, ${flipY})`;
      const radius = shapeType === 'circle' ? '50%' : element.radius || 8;
      const fillPaint = getShapeFillPaint(element);
      const strokePaint = getShapeStrokePaint(element);
      const fillEnabled = element.fillEnabled !== false && !isLine && !isArrow;
      const strokeEnabled = element.strokeEnabled !== false;
      const strokeWidth = element.strokeWidth || 2;
      const fillStyle = !fillEnabled
        ? { backgroundColor: 'transparent' }
        : fillPaint.type === 'gradient'
          ? { backgroundImage: paintToCss(fillPaint), backgroundColor: 'transparent' }
          : { backgroundColor: paintToCss(fillPaint) };
      const strokeStyle = !strokeEnabled || strokeWidth <= 0
        ? { border: 'none' }
        : strokePaint.type === 'gradient'
          ? {
              border: `${strokeWidth}px solid transparent`,
              borderImage: `${paintToCss(strokePaint)} 1`,
            }
          : {
              border: `${strokeWidth}px solid ${paintToCss(strokePaint)}`,
            };

      if (isLine || isArrow) {
        const lineCap = element.lineCap || 'round';
        const strokeStyleValue = element.strokeStyle || 'solid';
        const dashArray = strokeStyleValue === 'dashed'
          ? '12 8'
          : strokeStyleValue === 'dotted'
            ? '2 8'
            : '0';
        const effectiveStrokeWidth = strokeEnabled ? strokeWidth : 0;
        const gradientId = `stroke-${element.id}`;
        const arrowId = `arrow-${element.id}`;
        const markerSize = element.arrowHeadSize || 16;
        const showStart = element.arrowHead === 'start' || element.arrowHead === 'both';
        const showEnd = element.arrowHead === 'end' || element.arrowHead === 'both';
        const paintStops = strokePaint.type === 'gradient'
          ? strokePaint.stops.map((stop) => {
              const color = paintToCss({ type: 'solid', color: stop.color, alpha: stop.alpha });
              return { color, pos: stop.pos };
            })
          : [];

        return (
          <div
            {...commonProps}
            className={`studio-element ${isSelected ? 'is-selected' : ''} ${
              element.locked ? 'is-locked' : ''
            }`}
            style={{
              ...baseStyle,
              transform,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${element.w} ${element.h}`}
              preserveAspectRatio="none"
            >
              {strokePaint.type === 'gradient' && (
                <defs>
                  <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                    {paintStops.map((stop, index) => (
                      <stop key={`${gradientId}-${index}`} offset={`${stop.pos}%`} stopColor={stop.color} />
                    ))}
                  </linearGradient>
                  {isArrow && (
                    <marker
                      id={arrowId}
                      markerWidth={markerSize}
                      markerHeight={markerSize}
                      refX={markerSize * 0.7}
                      refY={markerSize / 2}
                      orient="auto"
                      markerUnits="userSpaceOnUse"
                    >
                      <path
                        d={`M0,0 L${markerSize},${markerSize / 2} L0,${markerSize} Z`}
                        fill={`url(#${gradientId})`}
                      />
                    </marker>
                  )}
                </defs>
              )}
              {strokePaint.type !== 'gradient' && isArrow && (
                <defs>
                  <marker
                    id={arrowId}
                    markerWidth={markerSize}
                    markerHeight={markerSize}
                    refX={markerSize * 0.7}
                    refY={markerSize / 2}
                    orient="auto"
                    markerUnits="userSpaceOnUse"
                  >
                    <path
                      d={`M0,0 L${markerSize},${markerSize / 2} L0,${markerSize} Z`}
                      fill={paintToCss(strokePaint)}
                    />
                  </marker>
                </defs>
              )}
              <line
                x1={0}
                y1={element.h / 2}
                x2={element.w}
                y2={element.h / 2}
                stroke={strokePaint.type === 'gradient' ? `url(#${gradientId})` : paintToCss(strokePaint)}
                strokeWidth={effectiveStrokeWidth}
                strokeLinecap={lineCap === 'square' ? 'square' : lineCap === 'butt' ? 'butt' : 'round'}
                strokeDasharray={dashArray === '0' ? undefined : dashArray}
                markerStart={isArrow && showStart ? `url(#${arrowId})` : undefined}
                markerEnd={isArrow && showEnd ? `url(#${arrowId})` : undefined}
              />
            </svg>
          </div>
        );
      }

      const customPath = CUSTOM_SHAPE_PATHS[shapeType];
      if (isGraphic || customPath) {
        const strokeWidthValue = strokeEnabled ? strokeWidth : 0;
        const strokeWidthSvg = strokeWidthValue > 0
          ? Math.max(0.2, (strokeWidthValue / Math.max(element.w, element.h, 1)) * 100)
          : 0;
        const fillGradient = fillEnabled && fillPaint.type === 'gradient';
        const strokeGradient = strokeEnabled && strokePaint.type === 'gradient';
        const fillId = `fill-${element.id}`;
        const strokeId = `stroke-${element.id}`;
        const fillValue = !fillEnabled
          ? 'none'
          : fillGradient
            ? `url(#${fillId})`
            : paintToCss(fillPaint);
        const strokeValue = !strokeEnabled
          ? 'none'
          : strokeGradient
            ? `url(#${strokeId})`
            : paintToCss(strokePaint);
        const graphicColor = fillEnabled
          ? getPaintSolidCss(fillPaint, '#111111')
          : getPaintSolidCss(strokePaint, '#111111');

        return (
          <div
            {...commonProps}
            className={`studio-element ${isSelected ? 'is-selected' : ''} ${
              element.locked ? 'is-locked' : ''
            }`}
            style={{
              ...baseStyle,
              transform,
            }}
          >
            {isGraphic ? (
              <div
                className="studio-graphic-svg"
                style={{ color: graphicColor }}
                dangerouslySetInnerHTML={{ __html: element.svg }}
              />
            ) : (
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <defs>
                  {fillGradient && buildSvgGradient(fillPaint, fillId)}
                  {strokeGradient && buildSvgGradient(strokePaint, strokeId)}
                </defs>
                <path
                  d={customPath}
                  fill={fillValue}
                  stroke={strokeValue}
                  strokeWidth={strokeWidthSvg || undefined}
                  strokeLinecap={element.lineCap || 'round'}
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            )}
          </div>
        );
      }

      return (
        <div
          {...commonProps}
          className={`studio-element ${isSelected ? 'is-selected' : ''} ${
            element.locked ? 'is-locked' : ''
          }`}
          style={{
            ...baseStyle,
            transform,
            ...fillStyle,
            borderRadius: radius,
            ...strokeStyle,
          }}
        />
      );
    }

    if (element.type === 'frame') {
      const isCropActive =
        Boolean(frameCropState?.active) && frameCropState.frameId === element.id;
      return (
        <FrameElement
          key={element.id}
          element={element}
          isSelected={isSelected}
          isSpacePressed={isSpacePressed}
          isCropActive={isCropActive}
          onCropPointerDown={onFrameCropPointerDown}
          onPointerDown={commonProps.onPointerDown}
          isDropHover={hoveredFrameId === element.id}
          debugMode={debugMode}
        />
      );
    }

    if (element.type === 'image') {
      return (
        <ImageElement
          key={element.id}
          element={element}
          isSelected={isSelected}
          isSpacePressed={isSpacePressed}
          onPointerDown={commonProps.onPointerDown}
          activeStroke={isEraserTarget ? activeEraserStroke : null}
        />
      );
    }

    return null;
  };

  useNonPassiveWheel(viewportRef, stageControls?.handleWheelZoom, onWheelOverride);

  return (
    <div
      ref={viewportRef}
      className={`studio-editor-workspace ${
        isSpacePressed ? 'pan-ready' : ''
      } ${isPanning ? 'is-panning' : ''} ${
        eraserMode === 'pixel' ? 'is-eraser' : ''
      } ${
        eraserMode === 'magic' ? 'is-magic' : ''
      } ${eyedropperActive ? 'is-eyedropper' : ''} ${
        showBackgroundImage ? 'has-bg-image' : ''
      } ${isBackgroundRepositioning ? 'is-bg-reposition' : ''}`}
      onPointerDown={handleWorkspacePointerDown}
      onPointerMove={handleWorkspacePointerMove}
      onPointerUp={handleWorkspacePointerUp}
      onPointerLeave={handleWorkspacePointerUp}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="studio-workspace-frame" aria-hidden="true" />
      <div
        className="studio-editor-stage-layer"
        ref={stageRef}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        }}
      >
        <div
          className="studio-artboard"
          ref={artboardRef}
          style={{
            width: artboardWidth,
            height: artboardHeight,
            ...getBackgroundStyle(),
          }}
        >
          {showBackgroundImage && (
            <div className="studio-artboard-bg-image">
              <img
                src={backgroundImage.src}
                alt=""
                className="studio-artboard-bg-img"
                style={{
                  width: bgImageW,
                  height: bgImageH,
                  left: backgroundImage.offsetX || 0,
                  top: backgroundImage.offsetY || 0,
                  opacity: Math.max(0, Math.min(1, bgOpacity / 100)),
                }}
                draggable={false}
              />
            </div>
          )}
          {showGuides && (
            <div
              className="studio-artboard-guides"
              style={{ inset: `${marginInset}px` }}
            />
          )}
          {showMargin && !showGuides && (
            <div
              className="studio-artboard-guides"
              style={{ inset: `${marginInset}px` }}
            />
          )}
          {showBleed && bleedInset > 0 && (
            <div
              className="studio-artboard-bleed"
              style={{ inset: `${-bleedInset}px` }}
            />
          )}
          {showGrid && <div className="studio-artboard-grid" style={getGridStyle()} />}
          {elements
            .slice()
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
            .map(renderElement)}
          {cropTarget && cropRect && (
            <div
              className="studio-crop-layer"
              style={{
                left: cropTarget.x,
                top: cropTarget.y,
                width: cropTarget.w,
                height: cropTarget.h,
              }}
            >
              <div
                className="studio-crop-rect"
                style={{
                  left: cropRect.x * cropTarget.w,
                  top: cropRect.y * cropTarget.h,
                  width: cropRect.w * cropTarget.w,
                  height: cropRect.h * cropTarget.h,
                }}
                onPointerDown={(event) => {
                  if (!onCropPointerDown) return;
                  event.preventDefault();
                  event.stopPropagation();
                  onCropPointerDown(event, 'move');
                }}
              >
                <div className="studio-crop-grid" />
                {['nw', 'ne', 'sw', 'se'].map((handle) => (
                  <div
                    key={handle}
                    className={`studio-crop-handle ${handle}`}
                    onPointerDown={(event) => {
                      if (!onCropPointerDown) return;
                      event.preventDefault();
                      event.stopPropagation();
                      onCropPointerDown(event, handle);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {selectionBounds && (
        <TransformBox
          bounds={selectionBounds}
          handles={isTextSelection ? ['nw', 'ne', 'sw', 'se'] : undefined}
          onHandlePointerDown={onTransformHandlePointerDown}
          onRotatePointerDown={onRotatePointerDown}
        />
      )}
      {marquee && (
        <div
          className="studio-marquee"
          style={{
            left: marquee.x,
            top: marquee.y,
            width: marquee.w,
            height: marquee.h,
          }}
        />
      )}
      {snapGuides?.x?.map((line) => (
        <div key={`x-${line}`} className="studio-guide-line x" style={{ left: line }} />
      ))}
      {snapGuides?.y?.map((line) => (
        <div key={`y-${line}`} className="studio-guide-line y" style={{ top: line }} />
      ))}
      {resizeHud && (
        <div
          className="studio-resize-hud"
          style={{ left: resizeHud.x, top: resizeHud.y }}
        >
          {resizeHud.label}
        </div>
      )}
      {eraserCursor && eraserCursor.mode !== 'magic' && (
        <div
          className="studio-eraser-cursor"
          style={{
            left: eraserCursor.x - eraserCursor.size / 2,
            top: eraserCursor.y - eraserCursor.size / 2,
            width: eraserCursor.size,
            height: eraserCursor.size,
          }}
        />
      )}
      {eraserCursor && eraserCursor.mode === 'magic' && (
        <div
          className="studio-magic-cursor"
          style={{
            left: eraserCursor.x,
            top: eraserCursor.y,
          }}
        >
          <div className="studio-magic-reticle" />
          <div className="studio-magic-label">{eraserCursor.label}</div>
        </div>
      )}
      <div className="studio-workspace-hud">
        <div className="studio-workspace-zoom">{Math.round(zoom * 100)}%</div>
        <div className="studio-workspace-hint">Space + Drag to pan</div>
      </div>
    </div>
  );
};

export default CanvasStage;
